import React, { Fragment } from "react";

import { Ellipsis, Eye, Users } from "lucide-react";

export const RiskClustering = () => {
    return (
        <div className='card card--md card--radius-md card--hover-shadow w-full dashboard__mcsa__risk_clustering'>
            <div className='d-flex justify-content-between align-items-center mb-xl'>
                <div>
                    <p className='text-lg text-semibold'>3-Axis Risk Clustering</p>
                    <p className='text-sm color--txt-muted'>Team positions in cognitive risk space</p>
                </div>
                <div className='d-flex align-items-center gap-sm'>
                    <button className={`btn btn--icon-md btn--radius-sm btn--secondary btn--selected`}>
                        <Eye />
                    </button>
                    <button className={`btn btn--icon-md btn--radius-sm btn--secondary`}>
                        <Ellipsis />
                    </button>
                </div>
            </div>
            <JudgmentBiasChart />
            <div className="d-flex justify-content-between gap-lg mt-lg">
                <div className="d-flex align-items-center gap-lg">
                    <div className="d-flex align-items-center gap-sm">
                        <div className='w-lg h-lg br-full bg--error' />
                        <p className="text-sm color--txt-tertiary text-medium">High Risk</p>
                    </div>
                    <div className="d-flex align-items-center gap-sm">
                        <div className='w-lg h-lg br-full bg--warning' />
                        <p className="text-sm color--txt-tertiary text-medium">Medium Risk</p>
                    </div>
                    <div className="d-flex align-items-center gap-sm">
                        <div className='w-lg h-lg br-full bg--success' />
                        <p className="text-sm color--txt-tertiary text-medium">Low Risk</p>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-sm">
                    <Users className="w-lg h-lg color--txt-tertiary" />
                    <p className="text-sm color--txt-tertiary text-medium">Bubble size = team size</p>
                </div>
            </div>
        </div>
    );
};

interface BubblePoint {
    x: number; // 0–100 (Cognitive Security Score)
    y: number; // 0–100 (Access Privilege)
    value: number;
    color: string;
};

const bubbleData: BubblePoint[] = [
    { x: 55, y: 72, value: 89, color: "#FF4D8D" },
    { x: 58, y: 65, value: 56, color: "#FF4D8D" },

    { x: 63, y: 58, value: 23, color: "#FBBF24" },
    { x: 61, y: 54, value: 42, color: "#FBBF24" },

    { x: 72, y: 45, value: 12, color: "#34D399" },
    { x: 75, y: 42, value: 28, color: "#34D399" },
];

const scaleX = (x: number, width: number, pad: number) =>
    pad + (x / 100) * (width - pad * 2);

const scaleY = (y: number, height: number, pad: number) =>
    pad + ((100 - y) / 100) * (height - pad * 2);

const Grid = ({ width, height, pad }: any) => {
    const steps = 4;

    return (
        <Fragment>
            {Array.from({ length: steps + 1 }).map((_, i) => {
                const x = pad + (i * (width - pad * 2)) / steps;
                const y = pad + (i * (height - pad * 2)) / steps;

                return (
                    <g key={i}>
                        <line x1={x} y1={pad} x2={x} y2={height - pad} stroke="rgba(0,0,0,0.08)" />
                        <line x1={pad} y1={y} x2={width - pad} y2={y} stroke="rgba(0,0,0,0.08)" />
                    </g>
                );
            })}
        </Fragment>
    );
};

const Bubble = ({ cx, cy, r, color, label }: any) => (
    <Fragment>
        <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={color}
            opacity={0.9}
            filter="url(#glow)"
        />
        <text
            x={cx}
            y={cy + 4}
            textAnchor="middle"
            fontSize={12}
            fontWeight="600"
            fill="white"
        >
            {label}
        </text>
    </Fragment>
);

const WIDTH = 480;
const HEIGHT = 256;
const PAD = 20;

const JudgmentBiasChart = () => {
    return (
        <div
            style={{
                background: "radial-gradient(circle at top,rgb(255, 255, 255) 0%,rgb(221, 221, 221) 100%)",
                borderRadius: 16,
                padding: 24,
                color: "black",
            }}
        >
            <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" height={HEIGHT}>
                <defs>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="6" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Grid */}
                <Grid width={WIDTH} height={HEIGHT} pad={PAD} />

                {/* Axis labels */}
                <text x={WIDTH / 2} y={HEIGHT - 24} textAnchor="middle">
                    Cognitive Security Score →
                </text>

                <text
                    x={24}
                    y={HEIGHT / 2}
                    transform={`rotate(-90 24 ${HEIGHT / 2})`}
                    textAnchor="middle"
                >
                    ← Access Privilege
                </text>

                {/* Quadrant labels */}
                {/* <text x={PAD - 10} y={PAD - 30} fill="#A1A1AA" fontSize={12}>
                    Low Judgment
                    <tspan x={PAD - 10} dy="14">High Bias</tspan>
                </text>

                <text x={WIDTH - PAD + 10} y={PAD - 30} fill="#A1A1AA" fontSize={12}>
                    High Judgment
                    <tspan x={WIDTH - PAD + 10} dy="14">High Bias</tspan>
                </text>

                <text x={PAD - 10} y={HEIGHT - PAD + 20} fill="#A1A1AA" fontSize={12}>
                    Low Judgment
                    <tspan x={PAD - 10} dy="14">Low Bias</tspan>
                </text>

                <text x={WIDTH - PAD + 10} y={HEIGHT - PAD + 20} fill="#A1A1AA" fontSize={12}>
                    High Judgment
                    <tspan x={WIDTH - PAD + 10} dy="14">Low Bias</tspan>
                </text> */}

                {/* Bubbles */}
                {bubbleData.map((b, i) => {
                    const cx = scaleX(b.x, WIDTH, PAD);
                    const cy = scaleY(b.y, HEIGHT, PAD);
                    const r = 14 + b.value * 0.2;

                    return (
                        <Bubble
                            key={i}
                            cx={cx}
                            cy={cy}
                            r={r}
                            color={b.color}
                            label={b.value}
                        />
                    );
                })}
            </svg>
        </div>
    );
};